import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/App';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { UserPlus } from 'lucide-react';

const Register = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await register(email, password, firstName, lastName);
      navigate('/dashboard');
    } catch (err) {
      setError(err.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-cloud-white flex items-center justify-center px-6" data-testid="register-page">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <Link to="/">
            <img
              src="https://customer-assets.emergentagent.com/job_4e3d1843-41fa-499c-be06-175903ad91f6/artifacts/pmd7efeu_2.%20FearlessIt-horizontal_official.png"
              alt="Fearless IT"
              className="h-10 mx-auto mb-6"
              data-testid="register-logo"
            />
          </Link>
          <h1 className="text-3xl font-bold text-carbon-black" data-testid="register-title">Create Account</h1>
          <p className="text-slate-gray mt-2">Start your journey with Fearless IT</p>
        </div>

        <Card className="shadow-xl rounded-2xl border-0">
          <CardHeader>
            <CardTitle className="text-center">Sign Up</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6" data-testid="register-form">
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg text-sm" data-testid="register-error">
                  {error}
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-carbon-black mb-2">First Name</label>
                  <input
                    type="text"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    className="w-full px-4 py-3 rounded-lg border border-cool-gray focus:outline-none focus:ring-2 focus:ring-fearless-blue"
                    placeholder="John"
                    required
                    data-testid="register-firstname-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-carbon-black mb-2">Last Name</label>
                  <input
                    type="text"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    className="w-full px-4 py-3 rounded-lg border border-cool-gray focus:outline-none focus:ring-2 focus:ring-fearless-blue"
                    placeholder="Doe"
                    required
                    data-testid="register-lastname-input"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-carbon-black mb-2">Email</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-4 py-3 rounded-lg border border-cool-gray focus:outline-none focus:ring-2 focus:ring-fearless-blue"
                  placeholder="you@company.com"
                  required
                  data-testid="register-email-input"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-carbon-black mb-2">Password</label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-4 py-3 rounded-lg border border-cool-gray focus:outline-none focus:ring-2 focus:ring-fearless-blue"
                  placeholder="••••••••"
                  required
                  minLength="6"
                  data-testid="register-password-input"
                />
              </div>

              <Button
                type="submit"
                className="w-full gradient-fearless text-white py-6 text-lg font-semibold"
                disabled={loading}
                data-testid="register-submit-btn"
              >
                {loading ? 'Creating account...' : (
                  <>
                    <UserPlus className="mr-2 h-5 w-5" />
                    Create Account
                  </>
                )}
              </Button>
            </form>

            <div className="mt-6 text-center">
              <p className="text-slate-gray text-sm">
                Already have an account?{' '}
                <Link to="/login" className="text-fearless-blue font-semibold hover:underline" data-testid="register-login-link">
                  Sign in
                </Link>
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Register;
